import { ReactNode, useCallback, useEffect, useRef } from 'react';
import { QueryKey, useQuery, useQueryClient } from '@tanstack/react-query';
import camelcaseKeys from 'camelcase-keys';

import { hcsConsole } from '@hcs/console';
import { PaginatedResponseData } from '@hcs/types';

import { OrderManagerApi, ViewOrderApi } from '../api';
import { ORDER_MANAGER_WEB_SOCKET_URL } from '../constants';
import { ORDER_SET_TO_STATUSES, ORDER_SETS } from '../constants';
import {
  OrdersQueryKey,
  QUERY_KEY_ORDER,
  QUERY_KEY_ORDER_ITEMS,
  QUERY_KEY_ORDERS,
} from '../hooks';
import {
  OmOrder,
  OrderItem,
  OrderManagerWebsocketMessage,
  OrderSets,
  OrderStatus,
} from '../types';

import { OrderUpdatesContext } from './OrderUpdates.context';

const orderStatusBelongsInOrderSet = (orderStatus: OrderStatus): OrderSets => {
  if (ORDER_SET_TO_STATUSES[ORDER_SETS.active]?.indexOf(orderStatus) !== -1) {
    return ORDER_SETS.active;
  } else if (
    ORDER_SET_TO_STATUSES[ORDER_SETS.completed]?.indexOf(orderStatus) !== -1
  ) {
    return ORDER_SETS.completed;
  } else if (
    ORDER_SET_TO_STATUSES[ORDER_SETS.cancelled]?.indexOf(orderStatus) !== -1
  ) {
    return ORDER_SETS.cancelled;
  }
  return ORDER_SETS.all;
};

const createMessageId = (message: OrderManagerWebsocketMessage) => {
  if (message.stream === 'order') {
    return `${message.payload.orderId}-${message.payload.action}-${message.payload.data.updatedAt}`;
  }
  return undefined;
};

// Keep track of messages received. OM sends duplicate messages and we only want to call the callback once
const ORDER_MESSAGES_RECEIVED: Record<string, true> = {};

export const OrderUpdatesProvider = ({ children }: { children: ReactNode }) => {
  const websocket = useRef<WebSocket | null>(null);
  const queryClient = useQueryClient();
  const ordersToReload = useRef(new Set<number>());
  // map of order id to Set of item ids
  const orderItemsToReload = useRef(new Map<number, Set<number>>());
  const ordersShouldInvalidate = useRef(false);
  const orderItemsShouldInvalidate = useRef(new Map<number, boolean>());

  // this token isn't needed outside of the context
  const tokenQuery = useQuery({
    queryKey: ['ORDER_MANAGER_WEBSOCKET_TOKEN'],
    queryFn: OrderManagerApi.websocketTokenExchange,
  });
  const token = tokenQuery.data?.token;

  // assumption is made that there's only one query that contains the order list
  const findInOrdersCache = useCallback(
    (
      orderId: number,
    ): {
      foundInOrdersCacheQk: OrdersQueryKey;
      foundInOrdersCachedData: PaginatedResponseData<OmOrder[]>;
    } | null => {
      let foundInOrdersCacheQk: QueryKey | null = null;
      let foundInOrdersCachedData: PaginatedResponseData<OmOrder[]> | null =
        null;
      const queryCache = queryClient.getQueriesData<
        PaginatedResponseData<OmOrder[]>
      >({
        queryKey: [QUERY_KEY_ORDERS],
      });
      queryCache.forEach(([cacheQk, cacheData]) => {
        if (cacheData) {
          const cachedOrder = cacheData?.data.find((order) => {
            return order.id === orderId;
          });
          if (cachedOrder) {
            foundInOrdersCacheQk = cacheQk;
            foundInOrdersCachedData = cacheData;
          }
        }
      });
      if (foundInOrdersCacheQk && foundInOrdersCachedData) {
        return {
          foundInOrdersCacheQk,
          foundInOrdersCachedData,
        };
      }
      return null;
    },
    [queryClient],
  );

  const findInOrderCache = useCallback(
    (
      orderId: number,
    ): {
      foundInOrderCacheQk: OrdersQueryKey;
      foundInOrderCachedData: OmOrder;
    } | null => {
      let foundInOrderCacheQk: QueryKey | null = null;
      let foundInOrderCachedData: OmOrder | null = null;
      const queryCache = queryClient.getQueriesData<OmOrder>({
        queryKey: [QUERY_KEY_ORDER],
      });
      queryCache.forEach(([cacheQk, cacheData]) => {
        if (cacheData && cacheData.id === orderId) {
          foundInOrderCacheQk = cacheQk;
          foundInOrderCachedData = cacheData;
        }
      });
      if (foundInOrderCacheQk && foundInOrderCachedData) {
        return {
          foundInOrderCacheQk,
          foundInOrderCachedData,
        };
      }
      return null;
    },
    [queryClient],
  );

  const addOrderToReload = useCallback((orderId: number) => {
    ordersToReload.current.add(orderId);
  }, []);

  const addOrderItemToReload = useCallback(
    (orderId: number, orderItemId: number) => {
      const orderItemSet = orderItemsToReload.current.get(orderId);
      if (orderItemSet) {
        orderItemSet.add(orderItemId);
      } else {
        orderItemsToReload.current.set(orderId, new Set([orderItemId]));
      }
    },
    [],
  );

  const invalidateOrdersAfterLoad = useCallback(() => {
    ordersShouldInvalidate.current = true;
  }, []);

  const fetchUpdatedOrders = useCallback(
    async (orderIds: number[]) => {
      const updatedOrders = await OrderManagerApi.fetchOrderIds(orderIds);
      const updatedOrdersById = updatedOrders.reduce<{ [id: number]: OmOrder }>(
        (accum, updatedOrder: OmOrder) => {
          accum[updatedOrder.id] = updatedOrder;
          return accum;
        },
        {},
      );

      // update orders list in place after fetch
      const queryCacheOrders = queryClient.getQueriesData<
        PaginatedResponseData<OmOrder[]>
      >({
        queryKey: [QUERY_KEY_ORDERS],
      });
      queryCacheOrders.forEach(([cacheQk, cacheData]) => {
        if (cacheData) {
          let hasUpdatedOrder = false;
          const newOrderList = cacheData?.data.map((order) => {
            const updatedOrder = updatedOrdersById[order.id];
            if (updatedOrder) {
              hasUpdatedOrder = true;
              return updatedOrder;
            } else {
              return order;
            }
          });
          if (hasUpdatedOrder) {
            queryClient.setQueryData<PaginatedResponseData<OmOrder[]>>(
              cacheQk,
              (existingData) => {
                if (!existingData) {
                  return existingData;
                }
                return {
                  pagination: existingData?.pagination,
                  data: newOrderList,
                };
              },
            );
          }
        }
      });

      const queryCacheOrder = queryClient.getQueriesData<OmOrder>({
        queryKey: [QUERY_KEY_ORDER],
      });
      queryCacheOrder.forEach(([cacheQk, cacheData]) => {
        if (cacheData) {
          const updatedOrder = updatedOrdersById[cacheData.id];
          if (updatedOrder) {
            queryClient.setQueryData<OmOrder>(cacheQk, updatedOrder);
          }
        }
      });
    },
    [queryClient],
  );

  const fetchUpdatedOrderItems = useCallback(
    async (orderId: number, itemIds: number[]) => {
      const updatedItems = await ViewOrderApi.fetchOrderItemIds(
        orderId,
        itemIds,
      );
      const updatedItemsById = updatedItems.reduce<{
        [id: number]: OrderItem;
      }>((accum, updatedItem: OrderItem) => {
        accum[updatedItem.id] = updatedItem;
        return accum;
      }, {});

      const queryCache = queryClient.getQueriesData<
        PaginatedResponseData<OrderItem[]>
      >({
        queryKey: [QUERY_KEY_ORDER_ITEMS],
      });
      queryCache.forEach(([cacheQk, cacheData]) => {
        if (cacheData) {
          let hasUpdatedItem = false;
          const newItemList = cacheData?.data.map((orderItem) => {
            const updatedItem = updatedItemsById[orderItem.id];
            if (updatedItem) {
              hasUpdatedItem = true;
              return updatedItem;
            } else {
              return orderItem;
            }
          });
          if (hasUpdatedItem) {
            queryClient.setQueryData<PaginatedResponseData<OrderItem[]>>(
              cacheQk,
              (existingData) => {
                if (!existingData) {
                  return existingData;
                }
                return {
                  pagination: existingData.pagination,
                  data: newItemList,
                };
              },
            );
          }
        }
      });
    },
    [queryClient],
  );

  useEffect(() => {
    const fetchOrdersAndItemsInteval = setInterval(() => {
      if (ordersToReload.current.size > 0) {
        fetchUpdatedOrders(Array.from(ordersToReload.current));
        ordersToReload.current.clear();
      }
      if (orderItemsToReload.current.size > 0) {
        orderItemsToReload.current.forEach((itemIds, orderId) => {
          fetchUpdatedOrderItems(orderId, Array.from(itemIds));
        });
        orderItemsToReload.current.clear();
      }
      if (ordersShouldInvalidate.current) {
        const ordersQueryState = queryClient.getQueryCache().find({
          exact: false,
          queryKey: [QUERY_KEY_ORDERS],
        });
        if (ordersQueryState?.state.status === 'success') {
          hcsConsole.logVerbose('OM-API: WS | ws invalidate orders');
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEY_ORDERS],
          });
          ordersShouldInvalidate.current = false;
        }
      }
      orderItemsShouldInvalidate.current.forEach(
        (shouldInvalidate, orderId) => {
          if (shouldInvalidate) {
            const orderItemsQueryState = queryClient.getQueryCache().find({
              exact: false,
              queryKey: [QUERY_KEY_ORDER_ITEMS, orderId],
            });
            if (orderItemsQueryState?.state.status === 'success') {
              hcsConsole.logVerbose('OM-API: WS | ws invalidate order items');
              queryClient.invalidateQueries({
                queryKey: [QUERY_KEY_ORDER_ITEMS, orderId],
              });
              orderItemsShouldInvalidate.current.delete(orderId);
            }
          }
        },
      );
    }, 5000);

    return () => {
      clearInterval(fetchOrdersAndItemsInteval);
    };
  }, [fetchUpdatedOrders, fetchUpdatedOrderItems, queryClient]);

  const createWebsocketConnection = useCallback(
    (token: string) => {
      websocket.current = new WebSocket(
        `${ORDER_MANAGER_WEB_SOCKET_URL}?token=${token}`,
      );
      websocket.current.onopen = () => {
        hcsConsole.log('OM-Api: WS | connected');
      };
      websocket.current.onmessage = (event: MessageEvent<string>) => {
        const message = camelcaseKeys<OrderManagerWebsocketMessage>(
          JSON.parse(event.data),
          {
            deep: true,
          },
        );
        if (message.stream !== 'debug') {
          const messageId = createMessageId(message);
          if (
            message.stream !== 'order' ||
            (messageId && !ORDER_MESSAGES_RECEIVED[messageId])
          ) {
            if (messageId) {
              // OM sends duplicate messages for the order stream. Only call the callback once.
              ORDER_MESSAGES_RECEIVED[messageId] = true;
            }
            if (message.stream === 'order') {
              const { action, orderId, data } = message.payload;
              // assuming the create order mutations will invalidate orders on success, so we only care about updates
              if (action === 'updated') {
                hcsConsole.logVerbose('OM-API: WS | order updated message', {
                  message,
                });
                // logic for orders list
                const { foundInOrdersCacheQk, foundInOrdersCachedData } =
                  findInOrdersCache(orderId) || {};
                if (foundInOrdersCacheQk && foundInOrdersCachedData) {
                  queryClient.setQueryData<PaginatedResponseData<OmOrder[]>>(
                    foundInOrdersCacheQk,
                    (existingData) => {
                      if (!existingData) {
                        return existingData;
                      }
                      const newData: PaginatedResponseData<OmOrder[]> = {
                        pagination: existingData.pagination,
                        data: foundInOrdersCachedData.data.map((order) => {
                          if (order.id === orderId) {
                            return {
                              ...order,
                              status: data.status,
                              cancelled: data.cancelled,
                            };
                          }
                          return order;
                        }),
                      };
                      return newData;
                    },
                  );
                }

                const belongsInOrderSet = orderStatusBelongsInOrderSet(
                  data.status,
                );
                // by default we choose all orders
                const selectedOrderSet =
                  foundInOrdersCacheQk?.[1]?.status || ORDER_SETS.all;
                // if the order belongs on the page, update all orders to get the full order update or to get the order on the page (if it isn't yet)
                if (
                  selectedOrderSet === ORDER_SETS.all ||
                  belongsInOrderSet === selectedOrderSet
                ) {
                  // if the updated order is currently on this page
                  if (foundInOrdersCacheQk) {
                    addOrderToReload(orderId);
                    // if the updated order isn't on this page, but should be
                  } else {
                    // reload with orders
                    hcsConsole.logVerbose(
                      'OM-API: WS | not found in orders cache but it should be here, invalidating',
                    );
                    invalidateOrdersAfterLoad();
                  }
                  // if the order no longer belongs on the page, but is on the page
                } else if (foundInOrdersCacheQk) {
                  hcsConsole.logVerbose(
                    'OM-API: WS | found in orders cache, but should not be here, invalidating',
                  );
                  // reload the orders list to remove the order from the page
                  invalidateOrdersAfterLoad();
                }

                // logic for order detail
                const { foundInOrderCacheQk } = findInOrderCache(orderId) || {};
                if (foundInOrderCacheQk) {
                  queryClient.setQueryData<OmOrder>(
                    foundInOrderCacheQk,
                    (existingData) => {
                      if (!existingData) {
                        return existingData;
                      }
                      return {
                        ...existingData,
                        status: data.status,
                        cancelled: data.cancelled,
                      };
                    },
                  );
                  // request full reload of order
                  addOrderToReload(orderId);
                }
              }
            } else if (message.stream === 'orderitem') {
              const { orderId, action } = message.payload;
              if (action === 'updated') {
                hcsConsole.logVerbose(
                  'OM-API: WS | orderitem updated message',
                  {
                    message,
                  },
                );
                // if the order is in the list cache, reload it to get updated item counts
                const { foundInOrdersCacheQk } =
                  findInOrdersCache(orderId) || {};
                if (foundInOrdersCacheQk) {
                  addOrderToReload(orderId);
                }

                // if item is in cache, update immediately, then request full reload
                const { id, status, cancelled } = message.payload.data;
                const messageOrderItemId = Number(id);
                const queryCache = queryClient.getQueriesData<
                  PaginatedResponseData<OrderItem[]>
                >({
                  queryKey: [QUERY_KEY_ORDER_ITEMS],
                });
                queryCache.forEach(([cacheQk, cacheData]) => {
                  if (cacheData) {
                    const cachedItem = cacheData?.data.find((item) => {
                      return item.id === messageOrderItemId;
                    });
                    if (cachedItem) {
                      queryClient.setQueryData<
                        PaginatedResponseData<OrderItem[]>
                      >(cacheQk, (existingData) => {
                        if (!existingData) {
                          return existingData;
                        }
                        return {
                          pagination: existingData.pagination,
                          data: cacheData.data.map((item) => {
                            if (item.id === messageOrderItemId) {
                              return {
                                ...item,
                                status,
                                cancelled,
                              };
                            }
                            return item;
                          }),
                        };
                      });
                      addOrderItemToReload(orderId, messageOrderItemId);
                    }
                  }
                });
              }
            }
          }
        }
      };
      websocket.current.onerror = async (event) => {
        // 401 handled in the websocket token exchange
        hcsConsole.log(`OM-Api: WS | Error`, {
          event: JSON.stringify(event),
        });
      };
    },
    [
      addOrderToReload,
      addOrderItemToReload,
      queryClient,
      invalidateOrdersAfterLoad,
      findInOrdersCache,
      findInOrderCache,
    ],
  );

  // Open and close connection
  useEffect(() => {
    // Do not attempt to connect if there isn't a token
    // Create the websocket connection if it does not exist
    if (token && !websocket.current) {
      createWebsocketConnection(token);
    }
    return () => {
      hcsConsole.log('OM-Api: disconnected');
      websocket.current?.close();
      websocket.current = null;
    };
  }, [createWebsocketConnection, token]);

  return (
    <OrderUpdatesContext.Provider value={null}>
      {children}
    </OrderUpdatesContext.Provider>
  );
};
